import { useState, useRef, useEffect, useCallback } from "react"
import {
  Bold,
  Italic,
  Heading2,
  Link,
  List,
  ListChecks,
  ListOrdered,
  Code2,
  TextQuote,
  X,
  Check,
} from "lucide-react"
import { commandsCtx, editorViewCtx } from "@milkdown/kit/core"
import {
  toggleStrongCommand,
  toggleEmphasisCommand,
  toggleInlineCodeCommand,
  toggleLinkCommand,
  updateLinkCommand,
  wrapInHeadingCommand,
  wrapInBulletListCommand,
  wrapInOrderedListCommand,
  wrapInBlockquoteCommand,
  isMarkSelectedCommand,
  strongSchema,
  emphasisSchema,
  linkSchema,
  inlineCodeSchema,
} from "@milkdown/kit/preset/commonmark"
import { posToDOMRect } from "@milkdown/kit/prose"
import { TextSelection } from "@milkdown/kit/prose/state"
import { useInstance } from "@milkdown/react"

interface ToolbarState {
  visible: boolean
  top: number
  left: number
  isBold: boolean
  isItalic: boolean
  isCode: boolean
  isLink: boolean
  linkHref: string
}

const initialState: ToolbarState = {
  visible: false,
  top: 0,
  left: 0,
  isBold: false,
  isItalic: false,
  isCode: false,
  isLink: false,
  linkHref: "",
}

export function FloatingToolbar() {
  const [state, setState] = useState<ToolbarState>(initialState)
  const [linkMode, setLinkMode] = useState(false)
  const [linkInput, setLinkInput] = useState("")
  const toolbarRef = useRef<HTMLDivElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const [loading, getInstance] = useInstance()

  const updateToolbar = useCallback(() => {
    const instance = getInstance()
    if (!instance) return

    try {
      const ctx = instance.ctx
      const view = ctx.get(editorViewCtx)
      const { empty, from, to } = view.state.selection
      const isTextSel = view.state.selection instanceof TextSelection
      const hasText = !empty && view.state.doc.textBetween(from, to).length > 0

      // On touch devices, skip focus check — tapping the toolbar briefly loses editor focus
      const isTouchDevice = "ontouchstart" in window
      if (!hasText || !isTextSel || (!isTouchDevice && !view.hasFocus())) {
        setState((prev) => (prev.visible ? { ...initialState } : prev))
        setLinkMode(false)
        return
      }

      const rect = posToDOMRect(view, from, to)
      const commands = ctx.get(commandsCtx)

      const isBold = commands.call(isMarkSelectedCommand.key, strongSchema.type(ctx)) as unknown as boolean
      const isItalic = commands.call(isMarkSelectedCommand.key, emphasisSchema.type(ctx)) as unknown as boolean
      const isCode = commands.call(isMarkSelectedCommand.key, inlineCodeSchema.type(ctx)) as unknown as boolean
      const isLink = commands.call(isMarkSelectedCommand.key, linkSchema.type(ctx)) as unknown as boolean

      // Get existing link href if any
      let linkHref = ""
      if (isLink) {
        const { doc, selection } = view.state
        doc.nodesBetween(selection.from, selection.to, (node) => {
          const linkMark = node.marks.find((m) => m.type === linkSchema.type(ctx))
          if (linkMark) {
            linkHref = linkMark.attrs.href || ""
          }
        })
      }

      // Calculate position relative to viewport
      const toolbarWidth = toolbarRef.current?.offsetWidth || 280
      const top = rect.top - 48
      const left = Math.max(8, rect.left + rect.width / 2 - toolbarWidth / 2)

      setState({
        visible: true,
        top,
        left,
        isBold,
        isItalic,
        isCode,
        isLink,
        linkHref,
      })
    } catch {
      // Editor not ready
    }
  }, [getInstance])

  // Listen for selection changes via the ProseMirror view's update plugin
  useEffect(() => {
    const instance = getInstance()
    if (!instance || loading) return

    let animFrame: number

    const handler = () => {
      cancelAnimationFrame(animFrame)
      animFrame = requestAnimationFrame(updateToolbar)
    }

    // Poll for selection changes via DOM events on the editor root
    try {
      const ctx = instance.ctx
      const view = ctx.get(editorViewCtx)
      const dom = view.dom

      dom.addEventListener("mouseup", handler)
      dom.addEventListener("keyup", handler)

      // selectionchange fires reliably on iOS when text is selected via touch
      document.addEventListener("selectionchange", handler)

      // On touch devices, dismiss native iOS selection menu by re-setting the selection
      const isTouchDevice = "ontouchstart" in window
      let dismissTimer: ReturnType<typeof setTimeout>
      const touchendHandler = isTouchDevice
        ? () => {
            clearTimeout(dismissTimer)
            dismissTimer = setTimeout(() => {
              const sel = window.getSelection()
              if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0).cloneRange()
                if (dom.contains(range.commonAncestorContainer)) {
                  sel.removeAllRanges()
                  sel.addRange(range)
                }
              }
            }, 10)
          }
        : null

      if (touchendHandler) {
        dom.addEventListener("touchend", touchendHandler)
      }

      // Handle Cmd/Ctrl+K for link
      const keydownHandler = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
          e.preventDefault()
          // Update toolbar state first
          updateToolbar()
          // Then switch to link mode
          setTimeout(() => {
            setState((prev) => {
              if (!prev.visible) return prev
              setLinkMode(true)
              setLinkInput(prev.linkHref)
              return prev
            })
          }, 0)
        }
      }

      dom.addEventListener("keydown", keydownHandler as EventListener)

      return () => {
        cancelAnimationFrame(animFrame)
        dom.removeEventListener("mouseup", handler)
        dom.removeEventListener("keyup", handler)
        document.removeEventListener("selectionchange", handler)
        if (touchendHandler) {
          dom.removeEventListener("touchend", touchendHandler)
          clearTimeout(dismissTimer)
        }
        dom.removeEventListener("keydown", keydownHandler as EventListener)
      }
    } catch {
      // Editor not ready
    }
  }, [loading, getInstance, updateToolbar])

  // Focus link input when entering link mode
  useEffect(() => {
    if (linkMode && linkInputRef.current) {
      linkInputRef.current.focus()
      linkInputRef.current.select()
    }
  }, [linkMode])

  function runCommand(fn: () => void) {
    fn()
    // Re-focus editor and update toolbar after command
    try {
      const instance = getInstance()
      if (instance) {
        const view = instance.ctx.get(editorViewCtx)
        view.focus()
      }
    } catch {}
    setTimeout(updateToolbar, 0)
  }

  function handleBold() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const commands = instance.ctx.get(commandsCtx)
      commands.call(toggleStrongCommand.key)
    })
  }

  function handleItalic() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const commands = instance.ctx.get(commandsCtx)
      commands.call(toggleEmphasisCommand.key)
    })
  }

  function handleHeading() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const commands = instance.ctx.get(commandsCtx)
      commands.call(wrapInHeadingCommand.key, 2)
    })
  }

  function handleLink() {
    setLinkMode(true)
    setLinkInput(state.linkHref)
  }

  function getListContext(instance: NonNullable<ReturnType<typeof getInstance>>) {
    const ctx = instance.ctx
    const commands = ctx.get(commandsCtx)
    const view = ctx.get(editorViewCtx)
    const { state } = view
    const { $from } = state.selection

    let listItemDepth = -1
    let listDepth = -1
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d)
      if (node.type.name === "list_item" && listItemDepth === -1) {
        listItemDepth = d
      }
      if ((node.type.name === "bullet_list" || node.type.name === "ordered_list") && listDepth === -1) {
        listDepth = d
      }
    }

    const listItem = listItemDepth > 0 ? $from.node(listItemDepth) : null
    const listNode = listDepth > 0 ? $from.node(listDepth) : null
    const isCheckbox = listItem?.attrs.checked != null
    const isOrdered = listNode?.type.name === "ordered_list"
    const isBullet = listNode?.type.name === "bullet_list" && !isCheckbox

    return { commands, view, state, $from, listItemDepth, listDepth, listItem, listNode, isCheckbox, isOrdered, isBullet }
  }

  function handleBulletList() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const { commands, view, state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered, isBullet } = getListContext(instance)

      if (listItemDepth === -1) {
        commands.call(wrapInBulletListCommand.key)
        return
      }

      if (isBullet) {
        // Already plain bullet → unwrap
        commands.call(wrapInBulletListCommand.key)
        return
      }

      // Convert from ordered or checkbox to plain bullet
      let tr = state.tr
      if (isOrdered) {
        tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.bullet_list)
      }
      if (isCheckbox && listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: null })
      }
      view.dispatch(tr)
    })
  }

  function handleOrderedList() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const { commands, view, state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered } = getListContext(instance)

      if (listItemDepth === -1) {
        commands.call(wrapInOrderedListCommand.key)
        return
      }

      if (isOrdered) {
        // Already ordered → unwrap
        commands.call(wrapInOrderedListCommand.key)
        return
      }

      // Convert from bullet/checkbox to ordered
      let tr = state.tr
      tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.ordered_list)
      if (isCheckbox && listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: null })
      }
      view.dispatch(tr)
    })
  }

  function handleCheckboxList() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const { commands, view, state, $from, listItemDepth, listDepth, listItem, isCheckbox, isOrdered } = getListContext(instance)

      if (listItemDepth === -1) {
        // Not in a list → create bullet list + add checked
        commands.call(wrapInBulletListCommand.key)
        const newState = view.state
        const $newFrom = newState.selection.$from
        for (let d = $newFrom.depth; d > 0; d--) {
          if ($newFrom.node(d).type.name === "list_item") {
            const tr = newState.tr.setNodeMarkup($newFrom.before(d), undefined, {
              ...$newFrom.node(d).attrs,
              checked: false,
            })
            view.dispatch(tr)
            break
          }
        }
        return
      }

      if (isCheckbox) {
        // Already checkbox → unwrap
        commands.call(wrapInBulletListCommand.key)
        return
      }

      // Convert from bullet or ordered to checkbox
      let tr = state.tr
      if (isOrdered) {
        tr = tr.setNodeMarkup($from.before(listDepth), state.schema.nodes.bullet_list)
      }
      if (listItem) {
        const pos = tr.mapping.map($from.before(listItemDepth))
        tr = tr.setNodeMarkup(pos, undefined, { ...listItem.attrs, checked: false })
      }
      view.dispatch(tr)
    })
  }

  function handleCode() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const commands = instance.ctx.get(commandsCtx)
      commands.call(toggleInlineCodeCommand.key)
    })
  }

  function handleBlockquote() {
    runCommand(() => {
      const instance = getInstance()
      if (!instance) return
      const commands = instance.ctx.get(commandsCtx)
      commands.call(wrapInBlockquoteCommand.key)
    })
  }

  function handleLinkApply() {
    const href = linkInput.trim()
    const instance = getInstance()
    if (!instance) return

    const commands = instance.ctx.get(commandsCtx)

    if (state.isLink && href) {
      // Update existing link
      commands.call(updateLinkCommand.key, { href })
    } else if (href) {
      // Add new link
      commands.call(toggleLinkCommand.key, { href })
    }

    setLinkMode(false)
    setLinkInput("")

    try {
      const view = instance.ctx.get(editorViewCtx)
      view.focus()
    } catch {}
    setTimeout(updateToolbar, 0)
  }

  function handleLinkRemove() {
    const instance = getInstance()
    if (!instance) return

    const commands = instance.ctx.get(commandsCtx)
    // Toggle off the link mark
    commands.call(toggleLinkCommand.key)

    setLinkMode(false)
    setLinkInput("")

    try {
      const view = instance.ctx.get(editorViewCtx)
      view.focus()
    } catch {}
    setTimeout(updateToolbar, 0)
  }

  function handleLinkCancel() {
    setLinkMode(false)
    setLinkInput("")
    try {
      const instance = getInstance()
      if (instance) {
        const view = instance.ctx.get(editorViewCtx)
        view.focus()
      }
    } catch {}
  }

  if (!state.visible) return null

  return (
    <div
      ref={toolbarRef}
      className="fixed z-50"
      style={{ top: state.top, left: state.left }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {linkMode ? (
        /* Link editing UI */
        <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-900 dark:bg-slate-100 shadow-xl shadow-slate-900/20 dark:shadow-black/40">
          <input
            ref={linkInputRef}
            type="text"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                handleLinkApply()
              }
              if (e.key === "Escape") {
                e.preventDefault()
                handleLinkCancel()
              }
            }}
            placeholder="Paste or type a link..."
            className="bg-transparent text-white dark:text-slate-900 text-xs outline-none w-48 px-1.5 py-1 placeholder:text-slate-500 dark:placeholder:text-slate-400"
          />
          <button
            onClick={handleLinkApply}
            className="p-1 rounded-md text-emerald-400 dark:text-emerald-600 hover:text-white dark:hover:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors"
            title="Apply link"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          {state.isLink && (
            <button
              onClick={handleLinkRemove}
              className="p-1 rounded-md text-red-400 dark:text-red-500 hover:text-white dark:hover:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors"
              title="Remove link"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          )}
          <button
            onClick={handleLinkCancel}
            className="p-1 rounded-md text-slate-500 dark:text-slate-400 hover:text-white dark:hover:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors"
            title="Cancel"
          >
            <span className="text-[10px] font-medium">Esc</span>
          </button>
        </div>
      ) : (
        /* Formatting toolbar */
        <div className="inline-flex items-center gap-0.5 px-1.5 py-1 rounded-lg bg-slate-900 dark:bg-slate-100 shadow-xl shadow-slate-900/20 dark:shadow-black/40">
          <ToolbarButton
            onClick={handleBold}
            active={state.isBold}
            title="Bold"
          >
            <Bold className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleItalic}
            active={state.isItalic}
            title="Italic"
          >
            <Italic className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleHeading}
            active={false}
            title="Heading"
          >
            <Heading2 className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            onClick={handleLink}
            active={state.isLink}
            title="Link (⌘K)"
          >
            <Link className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleBulletList}
            active={false}
            title="Bullet list"
          >
            <List className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleCheckboxList}
            active={false}
            title="Checkbox list"
          >
            <ListChecks className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleOrderedList}
            active={false}
            title="Numbered list"
          >
            <ListOrdered className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <Divider />
          <ToolbarButton
            onClick={handleCode}
            active={state.isCode}
            title="Inline code"
          >
            <Code2 className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
          <ToolbarButton
            onClick={handleBlockquote}
            active={false}
            title="Blockquote"
          >
            <TextQuote className="w-3.5 h-3.5" strokeWidth={2} />
          </ToolbarButton>
        </div>
      )}
    </div>
  )
}

function ToolbarButton({
  onClick,
  active,
  title,
  children,
}: {
  onClick: () => void
  active: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${
        active
          ? "text-white dark:text-slate-900 bg-slate-700 dark:bg-slate-300"
          : "text-slate-300 dark:text-slate-600 hover:text-white dark:hover:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-200"
      }`}
      title={title}
    >
      {children}
    </button>
  )
}

function Divider() {
  return (
    <div className="w-px h-4 bg-slate-700 dark:bg-slate-300 mx-0.5" />
  )
}
